import { MenuItem, Select, Skeleton, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import Alert from '/@/components/bayer/alert';
import DashboardCard from '/@/components/bayer/dashboard-card';
import { useFieldsByStageDashboard } from '/@/hooks/dashboards';
import { _ } from '/@/locale';
import { type CropType, PHENOLOGY_LABEL_KEYS, type PhenologyStage } from '/@/types/crop-monitoring';
import type { NodeScopeFilters } from '/@/utils/node-scope';
import CropDistributionBar from '../../../growers/components/growers-list/components/crop-distribution-bar';
import { getCrops } from '../../../hooks/crops';

const FieldsByStageDashboardCard = ({ node, nodeScope }: { node?: number; nodeScope: NodeScopeFilters }) => {
    const [crop, setCrop] = useState<CropType>('corn');
    const crops = getCrops();
    const fieldsByStageDashboardQuery = useFieldsByStageDashboard({
        crop,
        node,
        ...nodeScope,
    });
    const avaiableCrops = Object.values(crops).filter((crop) => crop.value !== 'fallow');
    const totalFields = useMemo(() => {
        const countByStage = fieldsByStageDashboardQuery.data?.countByStage || {};
        return Object.values(countByStage).reduce((sum: number, count: unknown) => sum + Number(count), 0);
    }, [fieldsByStageDashboardQuery.data?.countByStage]);
    return (
        <DashboardCard
            title={_('Lotes por estadío')}
            isLoading={fieldsByStageDashboardQuery.isValidating}
            headerRightContent={
                <Select value={crop} onChange={(e) => setCrop(e.target.value as CropType)} size='medium'>
                    {avaiableCrops.map((crop) => (
                        <MenuItem key={crop.value} value={crop.value}>
                            {crop.name}
                        </MenuItem>
                    ))}
                </Select>
            }
        >
            <Stack direction='column' justifyContent='space-between' spacing={2} height='100%'>
                <Stack direction='column'>
                    {fieldsByStageDashboardQuery.isValidating ? (
                        <Stack direction='row' spacing={1} justifyContent='start' flexWrap='wrap' useFlexGap>
                            {Array.from({ length: 4 }).map((_, index) => (
                                <Skeleton key={`stage-skeleton-${index}`} variant='text' width='100%' height={30} />
                            ))}
                        </Stack>
                    ) : (
                        Object.entries(fieldsByStageDashboardQuery.data?.countByStage || {})
                            .filter(([stage]) => stage !== 'harvested')
                            .map(([stage, count]) => (
                                <Stack direction='column' alignItems='start' key={stage}>
                                    <Typography variant='body2' color='primary'>
                                        {PHENOLOGY_LABEL_KEYS[stage as PhenologyStage] || stage}
                                    </Typography>
                                    <Stack
                                        direction='row'
                                        spacing={1}
                                        justifyContent='space-between'
                                        alignItems='center'
                                        sx={{ width: '100%' }}
                                    >
                                        <Stack sx={{ width: '100%', maxWidth: 275 }}>
                                            <CropDistributionBar
                                                distribution={[
                                                    {
                                                        crop,
                                                        percentage: Math.round((Number(count) / totalFields) * 100),
                                                        fields: Number(count),
                                                        color: crops['fallow']?.color!,
                                                        label: crops[crop as keyof typeof crops]?.name || '',
                                                    },
                                                    {
                                                        crop: 'empty',
                                                        percentage:
                                                            100 - Math.round((Number(count) / totalFields) * 100),
                                                        fields: totalFields - Number(count),
                                                        color: '#F5F5F5',
                                                        label: 'Resto',
                                                    },
                                                ]}
                                            />
                                        </Stack>
                                        <Typography
                                            variant='body2'
                                            color='primary'
                                            fontWeight='bold'
                                            sx={{ textWrap: 'nowrap' }}
                                        >
                                            {count} {_('Lotes')}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            ))
                    )}
                </Stack>
                {crop !== 'corn' && (
                    <Alert severity='surfaceVariant'>
                        {_('Por el momento, solo se muestran lotes en estadio barbecho o siembra de {{cropName}}', {
                            cropName: crops[crop]?.name,
                        })}
                    </Alert>
                )}
            </Stack>
        </DashboardCard>
    );
};

export default FieldsByStageDashboardCard;
